import { get, compose, map, flatten, uniqBy, mapValues, includes, sortBy, head, keys } from 'lodash/fp';
import { pipe } from 'fp-ts/function';
import { array, record } from 'fp-ts';
import { createScopedReducer } from '@mews-commander/utils';
import {
    ServiceCategoryPricing,
    ServicePricing,
    ServiceRateGroupPricing,
    ServiceRatePricing,
} from '@mews-commander/data-access';
import {
    RATE_ID_SELECTED,
    DEFAULT_RATES_CLEARED,
    MOBILE_RATE_GROUP_ID_SELECTED,
    PRICING_FETCH_SUCCEEDED,
    RESOURCE_CATEGORY_TYPE_SELECTED,
} from '../actions/types';

export interface ServiceRateGroupPricingExtended extends ServiceRateGroupPricing {
    defaultRateId: string | null | undefined;
    ratePrices: Array<ServiceRatePricing & { isRestricted: boolean }>;
}

// TODO: move getDefaultRateId and getRateGroupsFromPricing to utils directory
export const getDefaultRateId = (ratePrices: ServiceRatePricing[]) =>
    ratePrices.find(({ isRestricted }) => !isRestricted)?.rateId;

export const getRateGroupsFromPricing = (serviceCategoryPricings: ServiceCategoryPricing[]) => {
    let mobileRateGroupId: string | undefined;

    const rateGroups = pipe(
        serviceCategoryPricings,
        array.flatMap(({ occupancyPricing }) => occupancyPricing ?? []),
        array.flatMap(({ rateGroupPricing }) => rateGroupPricing ?? []),
        array.reduce({} as Record<string, ServiceRateGroupPricingExtended>, (accumulator, rateGroupPricing) => {
            const { rateGroupId, ratePrices, minRateId } = rateGroupPricing;
            mobileRateGroupId = rateGroupId;

            const newRatePrices = (ratePrices ?? []).map((ratePrice) => ({
                ...ratePrice,
                isRestricted:
                    accumulator[rateGroupId]?.ratePrices.find((r) => r.rateId === ratePrice.rateId)?.isRestricted ===
                    false
                        ? false
                        : ratePrice.isRestricted,
            }));

            return {
                ...accumulator,
                [rateGroupId]: {
                    ...rateGroupPricing,
                    defaultRateId: getDefaultRateId(newRatePrices) ?? minRateId,
                    rateIds: newRatePrices?.map(({ rateId }) => rateId) ?? [],
                    ratePrices: newRatePrices,
                },
            };
        })
    );

    return {
        rateGroups,
        mobileRateGroupId,
    };
};

export const getRatePricesFromPricing = compose(
    uniqBy('rateId'),
    sortBy('isRestricted'),
    flatten,
    map((p) =>
        map((r) => ({
            rateId: get('rateId', r),
            isRestricted: get('offer.isRestricted', r),
        }))((p as $TSFixMe).rates)
    )
);

export const getRateGroups = (ratePrices: $TSFixMe) =>
    compose(
        mapValues(({ minRateId, rateIds, ...other }) => {
            const filteredRatePrices = ratePrices.filter((r: ServiceRatePricing) => rateIds.includes(r.rateId));

            return {
                ...other,
                rateIds,
                minRateId,
                defaultRateId: getDefaultRateId(filteredRatePrices) ?? minRateId,
            };
        }),
        head,
        map('rateGroups')
    );

const getReorderedRateGroupsFromPricing = (pricing: $TSFixMe) => {
    const ratePrices = getRatePricesFromPricing(pricing);
    const rateGroups = getRateGroups(ratePrices)(pricing);

    return {
        ...rateGroups,
        mobileRateGroupId: head(keys(rateGroups)),
    };
};

type State = Record<string, ServiceRateGroupPricingExtended & { selectedRateId?: string }>;

export default createScopedReducer({} as State, {
    [RATE_ID_SELECTED]: (state: State, { rateGroupId, rateId }: { rateGroupId: string; rateId: string }) => ({
        ...state,

        [rateGroupId]: {
            ...get(rateGroupId, state),
            selectedRateId: rateId,
        },
    }),

    [MOBILE_RATE_GROUP_ID_SELECTED]: (state: State, rateGroupId: string) => ({
        ...state,
        mobileRateGroupId: rateGroupId,
    }),

    [RESOURCE_CATEGORY_TYPE_SELECTED]: (state: State, { pricing }: $TSFixMe) => {
        if (
            pricing &&
            pipe(
                pricing,
                // @ts-expect-error
                array.filter((pricingElement) => !record.isEmpty(pricingElement)),
                array.isNonEmpty
            )
        ) {
            return getReorderedRateGroupsFromPricing(pricing);
        }
        return state;
    },

    [DEFAULT_RATES_CLEARED]: () => {},

    [PRICING_FETCH_SUCCEEDED]: (
        state: State,
        {
            resourceCategoryTypeCategoryIds,
            servicePricing: { offer },
        }: { servicePricing: ServicePricing; resourceCategoryTypeCategoryIds: string[] }
    ) => {
        const filteredPricingByCategoryIds = offer.filter(({ categoryId }) =>
            includes(categoryId, resourceCategoryTypeCategoryIds)
        );

        if (array.isEmpty(filteredPricingByCategoryIds)) {
            return state;
        }

        const { mobileRateGroupId, rateGroups } = getRateGroupsFromPricing(filteredPricingByCategoryIds);

        return {
            mobileRateGroupId,
            ...pipe(
                rateGroups,
                record.mapWithIndex((rateGroupId, data) => {
                    const selectedRateId = state[rateGroupId]?.selectedRateId;
                    return {
                        ...data,
                        ...(selectedRateId !== undefined && { selectedRateId }),
                    };
                })
            ),
        };
    },
});
